import { ReplicantFromConfig } from '@play-co/replicant';
import config from '../config';

export const tuple = <T extends string>(...args: T[]) => {
  if (args.length === 0) {
    throw new Error(`Empty array invalid for tuple`);
  }

  return args as [T, ...T[]];
};

export type Tuple<T, TLength extends number> = [T, ...T[]] & {
  length: TLength;
};

export function isKey<T extends {}>(
  key: string | number | symbol,
  value: T,
): key is keyof T {
  return key in value;
}

// Used to widen a union type into a base type with any uncommon properties as optional
export type Widen<T> = [T] extends [Array<infer E>]
  ? { [K in keyof T]: Widen<T[K]> }
  : [T] extends [object]
    ? PartialKeys<
        { [K in AllKeys<T>]: Widen<Idx<T, K>> },
        Exclude<AllKeys<T>, keyof T> | OptionalKeys<T>
      >
    : T;
type PartialKeys<T, K extends keyof T> = Omit<T, K> &
  Partial<Pick<T, K>> extends infer O
  ? { [P in keyof O]: O[P] }
  : never;
type OptionalKeys<T> = T extends any
  ? { [K in keyof T]-?: {} extends Pick<T, K> ? K : never }[keyof T]
  : never;
type AllKeys<T> = T extends any ? keyof T : never;
type Idx<T, K extends PropertyKey, D = never> = T extends any
  ? K extends keyof T
    ? T[K]
    : D
  : never;

export type ReplicantConfig = ReplicantFromConfig<typeof config>;
export type {
  SyncActionAPI,
  AsyncActionAPI,
} from '../config/actions/createActions';
export type { AsyncGetterAPI } from '../config/asyncGetters/createAsyncGetters';
export type { ScheduledActionAPI } from '../config/scheduledActions/createScheduledActions';

export type WithId<Payload, Id = string> = { id: Id; payload: Payload };

export type RecordOfAllBut<
  Key extends string | number | symbol,
  Value,
  Exclude extends string | number | symbol,
> = Record<Key, Value> & Partial<Record<Exclude, void>>;

export type AtLeastOne<T> = [T, ...T[]];

export const exhaustiveTuple =
  <T extends string | number>() =>
  <L extends AtLeastOne<T>>(
    ...x: L extends any
      ? Exclude<T, L[number]> extends never
        ? L
        : Exclude<T, L[number]>[]
      : never
  ) =>
    x;

export type Immutable<T> = T extends (infer U)[]
  ? readonly Immutable<U>[]
  : T extends object
    ? {
        readonly [K in keyof T]: Immutable<T[K]>;
      }
    : T;

export type PartialRecord<
  Key extends symbol | string | number,
  Value,
> = Partial<Record<Key, Value>>;

export type DistributiveOmit<T, K extends PropertyKey> = T extends unknown
  ? Omit<T, K>
  : never;
